/**
 * WordPress dependencies
 */
import { BaseControl, Grid } from '@safe-wordpress/components';
import { useInstanceId } from '@safe-wordpress/compose';
import { useState } from '@safe-wordpress/element';
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import { useControlledState } from '../wordpress/components/utils';
import SelectControl from './select-control';
import LinkedButton from './linked-button';
import {
	ResetButton,
	LinkedButtonWrapper,
} from './styles/overflow-control-styles';
import {
	DEFAULT_VALUES,
	getInitialSide,
	isValueMixed,
	isValuesDefined,
	getAllowedSides,
} from './utils';
import type {
	OverflowControlIconProps,
	OverflowControlProps,
	OverflowControlValue,
} from './types';

const noop = () => {};

function useUniqueId( idProp?: string ) {
	const instanceId = useInstanceId(
		OverflowControl,
		'inspector-overflow-control'
	);

	return idProp || instanceId;
}
export function OverflowControl( {
	__next40pxDefaultSize = false,
	id: idProp,
	onChange = noop,
	label = _x( 'Box Control', 'text', 'nelio-ab-testing' ),
	values: valuesProp,
	sides = [ 'horizontal', 'vertical' ],
	disabledSides,
	resetValues = DEFAULT_VALUES,
	showAllSides = false,
	allowReset = true,
}: OverflowControlProps ): JSX.Element {
	const [ values, setValues ] = useControlledState( valuesProp, {
		initial: undefined,
		fallback: DEFAULT_VALUES,
	} );
	const inputValues = values || DEFAULT_VALUES;
	const hasInitialValue = isValuesDefined( valuesProp );
	const hasOneSide = sides?.length === 1;

	const [ isDirty, setIsDirty ] = useState( hasInitialValue );
	const [ isLinked, setIsLinked ] = useState(
		! hasInitialValue || ! isValueMixed( inputValues ) || hasOneSide
	);

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [ side, setSide ] = useState< OverflowControlIconProps[ 'side' ] >(
		getInitialSide( isLinked )
	);

	const id = useUniqueId( idProp );
	const headingId = `${ id }-heading`;

	const toggleLinked = () => {
		setIsLinked( ! isLinked );
		setSide( getInitialSide( ! isLinked ) );
		if ( ! isLinked ) {
			onChange( {
				horizontal: inputValues.horizontal,
				vertical: inputValues.horizontal,
			} );
		}
	};

	const handleOnFocus = ( nextSide: typeof side ) => () => {
		setSide( nextSide );
	};

	const handleOnChange = ( nextValues: OverflowControlValue ) => {
		onChange( nextValues );
		setValues( nextValues );
		setIsDirty( true );
	};

	const handleOnReset = () => {
		onChange( resetValues );
		setValues( resetValues );
		setIsDirty( false );
	};

	const selectControlProps = {
		onChange: handleOnChange,
		isLinked,
		sides,
		disabledSides,
		values: inputValues,
		__next40pxDefaultSize,
	};

	const sidesToRender = getAllowedSides( sides );

	return (
		<Grid
			id={ id }
			columns={ 3 }
			templateColumns="1fr min-content min-content"
			role="group"
			aria-labelledby={ headingId }
		>
			<BaseControl.VisualLabel id={ headingId }>
				{ label }
			</BaseControl.VisualLabel>
			{ ! showAllSides && isLinked && (
				<SelectControl
					side="all"
					onFocus={ handleOnFocus( 'all' ) }
					{ ...selectControlProps }
				/>
			) }
			{ ! showAllSides &&
				! hasOneSide &&
				! disabledSides.horizontal &&
				disabledSides.horizontal === disabledSides.vertical && (
					<LinkedButtonWrapper>
						<LinkedButton
							onClick={ toggleLinked }
							isLinked={ isLinked }
						/>
					</LinkedButtonWrapper>
				) }

			{ ( showAllSides || ! isLinked ) &&
				Array.from( sidesToRender ).map( ( axis ) => (
					<SelectControl
						key={ axis }
						side={ axis }
						onFocus={ handleOnFocus( axis ) }
						{ ...selectControlProps }
					/>
				) ) }
			{ allowReset && (
				<ResetButton
					className="component-overflow-control__reset-button"
					variant="secondary"
					size="small"
					onClick={ handleOnReset }
					disabled={ ! isDirty }
				>
					{ _x( 'Reset', 'command', 'nelio-ab-testing' ) }
				</ResetButton>
			) }
		</Grid>
	);
}
